import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { useApi, isOk } from "../../api";
import { useToast } from "../../components/MessageBox";
import { showConfirm } from "../../components/Dialog";
import { BiomeObservation, BiomeObservationRunSummary, VillageObservation } from "../../models/BiomeObservation";
import { formatDateTime } from "../../services/dateFormat";
import { useTranslation } from "../../hooks/useTranslation";
import BotScoped from "../../components/BotScoped";

export default function BiomeExplorerPage() {
    return <BotScoped><BiomeExplorer /></BotScoped>;
}

export function BiomeExplorer() {

    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [observations, setObservations] = createSignal<BiomeObservation[]>([]);
    const [runs, setRuns] = createSignal<BiomeObservationRunSummary[]>([]);
    const [villages, setVillages] = createSignal<VillageObservation[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [serverFilter, setServerFilter] = createSignal("");
    const [biomeFilter, setBiomeFilter] = createSignal("");
    const [selectedRunId, setSelectedRunId] = createSignal<string | null>(null);

    async function load() {
        setLoading(true);
        const [obsRes, runsRes, villagesRes] = await Promise.all([
            api.biomeObservation.getMine(),
            api.biomeObservation.getRuns(),
            api.villageObservation.getMine(),
        ]);
        if (isOk(obsRes.data)) setObservations((obsRes.data.value.observations ?? []) as BiomeObservation[]);
        if (isOk(runsRes.data)) setRuns((runsRes.data.value.runs ?? []) as BiomeObservationRunSummary[]);
        if (isOk(villagesRes.data)) setVillages((villagesRes.data.value.observations ?? []) as VillageObservation[]);
        setLoading(false);
    }

    async function deleteOne(id: string) {
        if (!await showConfirm(t("BIOME_DELETE_ONE_CONFIRM"), true)) return;
        const res = await api.biomeObservation.delete(id);
        if (isOk(res.data)) {
            setObservations(prev => prev.filter(o => o.id !== id));
            toast?.success(t("BIOME_DELETE_ONE_SUCCESS"));
        } else {
            toast?.error(t("BIOME_DELETE_ONE_FAILED"));
        }
    }

    async function deleteAll() {
        if (!await showConfirm(t("BIOME_DELETE_ALL_CONFIRM"), true)) return;
        const res = await api.biomeObservation.deleteAllMine();
        if (isOk(res.data)) {
            setObservations([]);
            setRuns([]);
            toast?.success(t("BIOME_DELETE_ALL_SUCCESS"));
        } else {
            toast?.error(t("BIOME_DELETE_ALL_FAILED"));
        }
    }

    function exportFile(runId?: string) {
        const params = new URLSearchParams();
        if (serverFilter()) params.set("server", serverFilter());
        if (runId) params.set("runId", runId);
        const url = "/api/BiomeObservation/export" + (params.toString() ? `?${params}` : "");
        const a = document.createElement("a");
        a.href = url;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    onMount(load);

    const servers = createMemo(() => {
        const seen = new Set<string>();
        for (const o of observations()) if (o.server) seen.add(o.server);
        return Array.from(seen).sort();
    });

    const visibleRuns = createMemo(() => {
        const serverValue = serverFilter();
        const all = runs();
        if (!serverValue) return all;
        return all.filter(r => r.server === serverValue);
    });

    const filtered = createMemo(() => {
        const serverValue = serverFilter();
        const biomeValue = biomeFilter().toLowerCase();
        const runValue = selectedRunId();
        return observations().filter(o => {
            if (serverValue && o.server !== serverValue) return false;
            if (biomeValue && !o.biomeName.toLowerCase().includes(biomeValue)) return false;
            if (runValue && o.runId !== runValue) return false;
            return true;
        });
    });

    const filteredVillages = createMemo(() => {
        const serverValue = serverFilter();
        const runValue = selectedRunId();
        return villages().filter(v => {
            if (serverValue && v.server !== serverValue) return false;
            if (runValue && v.runId !== runValue) return false;
            return true;
        });
    });

    const villageCountByRun = createMemo(() => {
        const counts = new Map<string, number>();
        for (const v of villages()) {
            if (!v.runId) continue;
            counts.set(v.runId, (counts.get(v.runId) ?? 0) + 1);
        }
        return counts;
    });

    function resolutionLabel(resolution: number): string {
        if (resolution === 0) return t("BIOME_RES_BLOCK");
        if (resolution === 1) return t("BIOME_RES_QUARTER");
        return "R" + resolution;
    }

    function shortRunId(id: string): string {
        return id.split("-")[0];
    }

    return (
        <div class="dashboard-page">
            <h1>{t("BIOME_EXPLORER_NAV")}</h1>
            <p class="gray">{t("BIOME_INTRO")}</p>

            <div class="h" style={{ gap: "var(--spacing-sm)", "align-items": "center", "flex-wrap": "wrap", "margin-bottom": "var(--spacing-md)" }}>
                <select
                    value={serverFilter()}
                    onInput={(e) => setServerFilter(e.currentTarget.value)}
                >
                    <option value="">{t("BIOME_ALL_SERVERS")}</option>
                    <For each={servers()}>
                        {s => <option value={s}>{s}</option>}
                    </For>
                </select>
                <input
                    type="text"
                    placeholder={t("BIOME_FILTER_PLACEHOLDER")}
                    value={biomeFilter()}
                    onInput={(e) => setBiomeFilter(e.currentTarget.value)}
                />
                <button onClick={load}>{t("REFRESH")}</button>
                <button onClick={() => exportFile(selectedRunId() ?? undefined)}>
                    <i class="fa-solid fa-download"></i>
                    {" "}{selectedRunId() ? t("BIOME_EXPORT_RUN") : t("BIOME_EXPORT_FILTERED")}
                </button>
                <Show when={observations().length > 0}>
                    <button onClick={deleteAll} class="danger">{t("BIOME_DELETE_ALL")}</button>
                </Show>
                <span class="gray" style={{ "margin-left": "auto" }}>
                    {filtered().length} / {observations().length} {t("BIOME_OBSERVATIONS")}
                </span>
            </div>

            <Show when={loading()}>
                <p class="gray">{t("LOADING")}</p>
            </Show>

            <Show when={!loading() && observations().length === 0}>
                <p class="gray">{t("BIOME_EMPTY")}</p>
            </Show>

            <Show when={!loading() && observations().length > 0}>
                <div class="h" style={{ gap: "var(--spacing-md)", "align-items": "flex-start" }}>
                    <div style={{
                        "min-width": "260px",
                        "max-width": "300px",
                        "flex-shrink": "0",
                    }}>
                        <h3 style={{ "margin-top": "0" }}>{t("BIOME_COLLECTION_RUNS")}</h3>
                        <div
                            class={`v ${selectedRunId() === null ? "selected" : ""}`}
                            style={{
                                gap: "2px",
                                padding: "var(--spacing-sm)",
                                cursor: "pointer",
                                "border-radius": "6px",
                                "background-color": selectedRunId() === null
                                    ? "var(--accent-primary, #7c4dff)"
                                    : "transparent",
                                color: selectedRunId() === null ? "#fff" : undefined,
                                "margin-bottom": "var(--spacing-xs)",
                            }}
                            onClick={() => setSelectedRunId(null)}
                        >
                            <strong>{t("BIOME_ALL_RUNS")}</strong>
                            <span class="gray" style={{ "font-size": "11px", color: "inherit", opacity: "0.85" }}>
                                {observations().length} {t("BIOME_OBSERVATIONS")}
                            </span>
                        </div>
                        <For each={visibleRuns()}>
                            {(run) => {
                                const isSelected = () => selectedRunId() === run.runId;
                                const villageCount = () => villageCountByRun().get(run.runId) ?? 0;
                                return (
                                    <div
                                        class="v"
                                        style={{
                                            gap: "2px",
                                            padding: "var(--spacing-sm)",
                                            cursor: "pointer",
                                            "border-radius": "6px",
                                            "background-color": isSelected()
                                                ? "var(--accent-primary, #7c4dff)"
                                                : "transparent",
                                            color: isSelected() ? "#fff" : undefined,
                                            "margin-bottom": "var(--spacing-xs)",
                                            border: "1px solid var(--border-color, rgba(255,255,255,0.08))",
                                        }}
                                        onClick={() => setSelectedRunId(run.runId)}
                                    >
                                        <div class="h" style={{ "justify-content": "space-between", "align-items": "center" }}>
                                            <strong>{shortRunId(run.runId)}</strong>
                                            <button
                                                title={t("BIOME_EXPORT_THIS_RUN")}
                                                style={{ padding: "2px 6px", "font-size": "11px" }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    exportFile(run.runId);
                                                }}
                                            >
                                                <i class="fa-solid fa-download"></i>
                                            </button>
                                        </div>
                                        <span style={{ "font-size": "11px", opacity: "0.85" }}>
                                            {run.observationCount} obs · {run.server || "?"}
                                        </span>
                                        <Show when={villageCount() > 0}>
                                            <span style={{
                                                "font-size": "11px",
                                                opacity: "0.95",
                                                color: isSelected() ? "#ffd54f" : "var(--accent-primary, #7c4dff)",
                                                "font-weight": "600",
                                            }}>
                                                <i class="fa-solid fa-house-chimney"></i> {villageCount()} {villageCount() === 1 ? t("BIOME_VILLAGE") : t("BIOME_VILLAGES")}
                                            </span>
                                        </Show>
                                        <span style={{ "font-size": "11px", opacity: "0.7" }}>
                                            {formatDateTime(run.completedAt)}
                                        </span>
                                    </div>
                                );
                            }}
                        </For>
                        <Show when={visibleRuns().length === 0}>
                            <p class="gray" style={{ "font-size": "12px" }}>{t("BIOME_NO_RUNS")}</p>
                        </Show>
                    </div>

                    <div style={{ flex: "1", "min-width": "0", "overflow-x": "auto" }}>
                        <Show when={filteredVillages().length > 0}>
                            <div style={{ "margin-bottom": "var(--spacing-md)" }}>
                                <h3 style={{ "margin-top": "0", "margin-bottom": "var(--spacing-xs)" }}>
                                    <i class="fa-solid fa-house-chimney"></i> {t("BIOME_VILLAGES_TITLE")}
                                    <span class="gray" style={{ "font-size": "13px", "margin-left": "var(--spacing-sm)", "font-weight": "normal" }}>
                                        {t("BIOME_VILLAGES_SUMMARY", filteredVillages().length, filteredVillages().reduce((sum, v) => sum + v.villagerCount, 0))}
                                    </span>
                                </h3>
                                <p class="gray" style={{ "font-size": "12px", "margin": "0 0 var(--spacing-xs) 0" }}>{t("BIOME_VILLAGES_INTRO")}</p>
                                <table class="sessiontable">
                                    <thead>
                                        <tr>
                                            <td>{t("BIOME_CHUNK_X")}</td>
                                            <td>{t("BIOME_CHUNK_Z")}</td>
                                            <td>{t("BIOME_VILLAGERS")}</td>
                                            <td>{t("BIOME_SERVER")}</td>
                                            <td>{t("BIOME_RUN")}</td>
                                            <td>{t("BIOME_DETECTED")}</td>
                                            <td>{t("BIOME_RECORDED")}</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={filteredVillages()}>
                                            {(v) => (
                                                <tr>
                                                    <td>{v.chunkX}</td>
                                                    <td>{v.chunkZ}</td>
                                                    <td>{v.villagerCount}</td>
                                                    <td>{v.server || <span class="gray">-</span>}</td>
                                                    <td>
                                                        {v.runId
                                                            ? <span title={v.runId}>{shortRunId(v.runId)}</span>
                                                            : <span class="gray">-</span>}
                                                    </td>
                                                    <td>{v.detectionMethod}</td>
                                                    <td>{formatDateTime(v.createdAt)}</td>
                                                </tr>
                                            )}
                                        </For>
                                    </tbody>
                                </table>
                            </div>
                        </Show>

                        <h3 style={{ "margin-top": "0", "margin-bottom": "var(--spacing-xs)" }}>
                            {t("BIOME_OBSERVATIONS_TITLE")}
                        </h3>
                        <table class="sessiontable">
                            <thead>
                                <tr>
                                    <td>{t("BIOME_BIOME")}</td>
                                    <td>ID</td>
                                    <td>X</td>
                                    <td>Y</td>
                                    <td>Z</td>
                                    <td>{t("BIOME_RES")}</td>
                                    <td>{t("BIOME_SERVER")}</td>
                                    <td>{t("BIOME_RUN")}</td>
                                    <td>{t("BIOME_SEED")}</td>
                                    <td>{t("BIOME_SOURCE")}</td>
                                    <td>{t("BIOME_RECORDED")}</td>
                                    <td></td>
                                </tr>
                            </thead>
                            <tbody>
                                <For each={filtered()}>
                                    {(obs) => (
                                        <tr>
                                            <td>{obs.biomeName}</td>
                                            <td>{obs.biomeId}</td>
                                            <td>{obs.blockX}</td>
                                            <td>{obs.blockY}</td>
                                            <td>{obs.blockZ}</td>
                                            <td>{resolutionLabel(obs.resolution)}</td>
                                            <td>{obs.server || <span class="gray">-</span>}</td>
                                            <td>
                                                {obs.runId
                                                    ? <span title={obs.runId}>{shortRunId(obs.runId)}</span>
                                                    : <span class="gray">-</span>}
                                            </td>
                                            <td>{obs.seedUsed}</td>
                                            <td>{obs.source}</td>
                                            <td>{formatDateTime(obs.createdAt)}</td>
                                            <td>
                                                <button onClick={() => deleteOne(obs.id)}>
                                                    <i class="fa-solid fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </For>
                            </tbody>
                        </table>
                    </div>
                </div>
            </Show>
        </div>
    );
}
